// For the logic here, refer to: sources/core-embed/core-embed-application/src/main/java/com/axpm/officecalendar/core/internal/query/AppointmentHistoryQuery.java
import {
  formatTzDate,
  parseLocalDatetimeStringToZonedDate,
  personName,
} from 'Data/format.js'
import { useMemo } from 'react'
import { useDataValue } from 'Simple/Data.js'
import { utcToZonedTime } from 'date-fns-tz'

/** @type {import('Simple/types.js').useDataTransform} */
export default function useDataTransform(props, data) {
  let time_zone_id = useDataValue({
    context: 'timegrid',
    viewPath: props.viewPath,
    path: 'location.time_zone_id',
  })

  return useMemo(() => {
    if (!data) return data

    let entries = []

    data.vaxiom_appointment_booking_state_changes.forEach(change => {
      let newState = change.new_state
      let stateChangeTime = formatTzDate(
        utcToZonedTime(new Date(`${change.change_time}Z`), time_zone_id),
        'MMM d, yyyy hh:mm a',
        time_zone_id
      )

      // Create appointmentTime from local_start_date and local_start_time if available
      let appointmentTime = null
      if (change.booking.local_start_date && change.booking.local_start_time) {
        // Combine date and time
        appointmentTime = formatTzDate(
          parseLocalDatetimeStringToZonedDate(
            `${change.booking.local_start_date}T${change.booking.local_start_time}`,
            time_zone_id
          ),
          'MMM d, yyyy hh:mm a',
          time_zone_id
        )
      }

      // Process based on state
      if (newState === 'SCHEDULED') {
        // Determine if this is initial scheduling or rescheduling
        let action = entries.length === 0 ? 'SCHEDULED' : 'RESCHEDULED'

        // Get creator name (for SCHEDULED actions we use the created_by)
        let user = personName(getCreatedBy(change.booking))
        user = user === '' ? 'N/A' : user

        // Add to entries
        entries.push({
          time: stateChangeTime,
          action: action,
          user,
          appointmentTime: appointmentTime,
        })
      } else {
        // For non-SCHEDULED states, get the modifier name
        let user = personName(getModifiedBy(change.booking))
        user = user === '' ? 'N/A' : user

        if (newState === 'OFFICE_CANCELLED') {
          entries.push({
            time: stateChangeTime,
            action: 'CANCELLED_BY_OFFICE',
            user,
          })
        } else if (newState === 'PATIENT_CANCELLED') {
          entries.push({
            time: stateChangeTime,
            action: 'CANCELLED_BY_PATIENT',
            user,
          })
        } else if (newState === 'NO_SHOW') {
          console.log({ change })
          entries.push({
            time: stateChangeTime,
            action: 'NO_SHOW',
            user,
            appointmentTime: appointmentTime,
          })
        }
      }
    })

    if (data.vaxiom_appointments && data.vaxiom_appointments.length > 0) {
      let appointment = data.vaxiom_appointments[0]
      let createdTime = formatTzDate(
        utcToZonedTime(new Date(`${appointment.sys_created}Z`), time_zone_id),
        'MMM d, yyyy hh:mm a',
        time_zone_id
      )

      let creatorName = appointment.created_by
        ? personName(appointment.created_by.person)
        : 'N/A'

      // Add CREATED entry at the beginning
      entries.unshift({
        time: createdTime,
        action: 'CREATED',
        user: creatorName,
      })
    }

    return entries
  }, [data, time_zone_id])
}

/**
 *
 * @param {Object} appointment_booking
 * @returns
 */
function getCreatedBy(appointment_booking) {
  // it is possible that it will return the wrong person if last_created_by was set to a wrong value during conversions
  // making sure it's showing it only when created by the patient
  let created_by_patient = appointment_booking.created_by_person?.patient
    ? appointment_booking.created_by_person
    : null
  return created_by_patient || appointment_booking.created_by?.person
}

/**
 *
 * @param {Object} appointment_booking
 * @returns
 */
function getModifiedBy(appointment_booking) {
  // it is possible that it will return the wrong person if last_updated_by was set to a wrong value during conversions
  // making sure it's showing it only when updated by the patient
  let updated_by_patient = appointment_booking.modified_by_person?.patient
    ? appointment_booking.modified_by_person
    : null
  return updated_by_patient || appointment_booking.modified_by?.person
}
